"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Menu, X, CreditCard, LayoutDashboard, LogIn, ArrowRight, BookOpen, Settings } from "lucide-react";

interface MobileNavProps {
  isLoggedIn: boolean;
}

export function MobileNav({ isLoggedIn }: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <div ref={menuRef} className="md:hidden">
      {/* Hamburger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative z-50 flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label="Toggle menu"
      >
        <div className="relative h-5 w-5">
          <Menu
            className={`absolute inset-0 size-5 transition-all duration-300 ${
              open ? "rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100"
            }`}
          />
          <X
            className={`absolute inset-0 size-5 transition-all duration-300 ${
              open ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-0 opacity-0"
            }`}
          />
        </div>
      </button>

      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-background/60 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setOpen(false)}
      />

      {/* Slide-down panel — dashboard card aesthetic (amber-tinted radial + amber-500/15 border + top accent line). */}
      <div
        className={`fixed left-0 right-0 top-[57px] z-40 overflow-hidden border-b border-amber-500/15 bg-[#1C1917] bg-[radial-gradient(circle_at_50%_0%,rgba(245,158,11,0.14),transparent_42%)] shadow-2xl shadow-amber-500/5 transition-all duration-300 ease-out ${
          open
            ? "translate-y-0 opacity-100"
            : "-translate-y-4 opacity-0 pointer-events-none"
        }`}
      >
        {/* Top hairline accent — mirrors dashboard hero card. */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/35 to-transparent"
        />

        <nav className="mx-auto max-w-md px-5 py-5 space-y-1.5">
          {isLoggedIn ? (
            <>
              <Link
                href="/dashboard"
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[15px] font-medium text-light/80 transition-all hover:bg-white/[0.04] hover:text-light"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 transition-colors group-hover:bg-amber-500/15">
                  <LayoutDashboard className="size-4" />
                </span>
                Dashboard
              </Link>

              <Link
                href="/pricing"
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[15px] font-medium text-light/80 transition-all hover:bg-white/[0.04] hover:text-light"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 transition-colors group-hover:bg-amber-500/15">
                  <CreditCard className="size-4" />
                </span>
                Pricing
              </Link>

              <Link
                href="/blog"
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[15px] font-medium text-light/80 transition-all hover:bg-white/[0.04] hover:text-light"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 transition-colors group-hover:bg-amber-500/15">
                  <BookOpen className="size-4" />
                </span>
                Blog
              </Link>

              <Link
                href="/settings"
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[15px] font-medium text-light/80 transition-all hover:bg-white/[0.04] hover:text-light"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 transition-colors group-hover:bg-amber-500/15">
                  <Settings className="size-4" />
                </span>
                Settings
              </Link>
            </>
          ) : (
            <>
              <Link
                href="/pricing"
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[15px] font-medium text-light/80 transition-all hover:bg-white/[0.04] hover:text-light"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 transition-colors group-hover:bg-amber-500/15">
                  <CreditCard className="size-4" />
                </span>
                Pricing
              </Link>

              <Link
                href="/blog"
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[15px] font-medium text-light/80 transition-all hover:bg-white/[0.04] hover:text-light"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 transition-colors group-hover:bg-amber-500/15">
                  <BookOpen className="size-4" />
                </span>
                Blog
              </Link>

              <Link
                href="/login"
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[15px] font-medium text-light/80 transition-all hover:bg-white/[0.04] hover:text-light"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 transition-colors group-hover:bg-amber-500/15">
                  <LogIn className="size-4" />
                </span>
                Sign In
              </Link>
            </>
          )}

          {/* CTA — dashboard primary action (amber→orange gradient, shadow glow). */}
          <div className="mt-3 pt-3 border-t border-white/[0.06]">
            <Link
              href="/app"
              onClick={() => setOpen(false)}
              className="group flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3.5 text-sm font-semibold text-white shadow-[0_0_30px_-5px_rgba(217,119,6,0.4)] transition-all hover:from-amber-400 hover:to-orange-400 hover:shadow-[0_0_40px_-5px_rgba(217,119,6,0.6)]"
            >
              Find my niche
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </nav>
      </div>
    </div>
  );
}
